import { Fragment, useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis, bindKeyboardScrollFix } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
// ⚠️ Onyx paylaşılan `WeddingProgram`'ı KULLANMAZ: programı kendi editoryal
// tipografisiyle tarih bölümünün içine yerleştiriyor (aşağıda `program.length > 1`).
// Ortak bileşen tema-nötr olduğu için burada iliştirilmiş duruyordu.
import { useTranslation } from "react-i18next";
import { useEventName } from "../../../../lib/wedding-event";
import { sizedUrl, ONYX_IMG_W } from "./media";
import { resolveActiveWeddingEvent, eventSourceFromRow } from "@wedding/shared";
import { OnyxCountdown } from "./OnyxCountdown";
import {
  type OnyxConfig,
  ONYX_DEFAULTS,
  makeTokenContext,
  resolveTokens,
} from "./config";

gsap.registerPlugin(ScrollTrigger, SplitText);

interface OnyxLayoutProps extends JoinLayoutProps {
  config?: OnyxConfig;
}

/**
 * ONYX — Saint Laurent / Haute Couture editorial layout.
 * All texts/colors/images come from `config` (see ./config.ts).
 */
export function OnyxLayout({ formSlot, program, title, dateTime, location, description, galleryImages, config, ambientVideoUrl }: OnyxLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const progressDotRef = useRef<HTMLDivElement>(null);

  const cfg = config ?? ONYX_DEFAULTS;

  // Tek etkinlikli düğünlerde canlı geri sayımın hedefi — ana tarih DEĞİL,
  // "en yakın gelecekteki etkinlik". (Sabit "Düğüne Kalan" metni de kalktı.)
  const { t: tr } = useTranslation();
  const eventName = useEventName();
  const activeEvent = resolveActiveWeddingEvent(
    eventSourceFromRow({ date_time: dateTime, location }),
  );
  const activeEventName = activeEvent ? eventName(activeEvent) : "";

  /**
   * `{tarih}`/`{mekan}` token'larını hangi etkinlik besliyor.
   *
   * İki etkinlikli düğünde kayan şerit + hero + RSVP metinleri bir tanesini
   * anmak zorunda. `auto` (varsayılan) = en yakın gelecekteki etkinlik → kına
   * geçince metinler kendiliğinden düğüne döner. Organizatör tema editöründen
   * sabitleyebilir.
   */
  const tokenSource =
    program.length > 1
      ? ((cfg.tokenEvent ?? "auto") === "primary"
          ? program.find((e) => e.key === "primary")
          : cfg.tokenEvent === "event2"
            ? program.find((e) => e.key === "event2")
            : program.find((e) => e.daysUntil >= 0) ?? program[program.length - 1])
      : undefined;

  const ctx = makeTokenContext({
    title,
    dateTime: tokenSource?.dateTime ?? dateTime,
    location: tokenSource?.location ?? location,
    // Tek etkinlikli düğünde de dolu olmalı: boş bırakınca şeritte "●●" gibi
    // sahipsiz ayraçlar kalıyordu (prod'da görüldü).
    eventName: tokenSource?.name ?? program[0]?.name ?? "",
  });
  const t = (s: string) => resolveTokens(s, ctx);

  const accent = cfg.colors.accent;
  const bg = cfg.colors.background;

  const pickImage = (idx: number) =>
    galleryImages[idx] ?? galleryImages[0] ?? "";

  // Boyutlandırma politikası `./media`'da; `collectMedia` preload'u da AYNI
  // helper'ı kullanır → preload edilen URL render edilenle birebir eşleşir
  // (yoksa cache ıskası → lookbook'ta siyah→"pat" beliriş).

  const looks = cfg.lookbook.looks.map((l) => ({
    ...l,
    img: l.imageUrl || galleryImages[0] || "",
  }));

  // Backdrop images for Date/Venue — may be "" when the wedding has no cover
  // images; the sections then fall back to a clean dark editorial backdrop.
  const dateBg = pickImage(cfg.story.imageIndex);
  const venueBg = pickImage(cfg.venue.imageIndex);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.5, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);
    // RSVP formunda klavye açılınca smooth-scroll ile native scroll-into-view
    // çekişmesin diye alan odaktayken Lenis'i duraklat.
    const detachKeyboardFix = bindKeyboardScrollFix(lenis);

    // Klavye ve mobil adres-çubuğu YALNIZ viewport yüksekliğini değiştirir.
    // ScrollTrigger bunu "gerçek resize" sanıp tüm pin'leri yeniden ölçüyor
    // (dahili %25 eşiği klavyeyi yakalamaz → refresh tetiklenir) ve sayfa
    // "bir kayıyor". `resize`'ı otomatik-refresh olaylarından çıkar; yeniden
    // ölçümü aşağıda SADECE genişlik değiştiğinde (yön değişimi / masaüstü)
    // kendimiz tetikleriz. Cleanup'ta eski davranış geri yüklenir.
    const DEFAULT_AUTO_REFRESH = "visibilitychange,DOMContentLoaded,load,resize";
    ScrollTrigger.config({ autoRefreshEvents: "visibilitychange,DOMContentLoaded,load" });

    let onResize: (() => void) | undefined;

    const gctx = gsap.context(() => {
      // Reveal tied to scroll position (scrub) — never skipped on fast scroll,
      // never flickers (no play/reverse), and once scrolled past, the element
      // is guaranteed at its natural state (progress=1) instead of stuck at
      // opacity:0 the way a missed `toggleActions` trigger would leave it.
      const reveal = (
        target: gsap.TweenTarget | null | undefined,
        fromVars: gsap.TweenVars,
        st: ScrollTrigger.Vars,
      ) => {
        if (!target) return;
        gsap.from(target, {
          ...fromVars,
          ease: fromVars.ease ?? "none",
          scrollTrigger: { scrub: true, start: "top 85%", end: "top 45%", ...st },
        });
      };

      // ── HERO title: fit to a single line at every viewport ──────────
      // A long "A & B" name at the static clamp() floor wrapped awkwardly on
      // narrow screens (half the name dropping to a second line). Measure the
      // name against the available width and scale the font so it always sits
      // on ONE line, capped so desktop stays grand. nowrap keeps SplitText at a
      // single line; re-fit on resize (font-size only — line count stays 1).
      const heroTitle = document.querySelector<HTMLElement>(".onx-hero-title");
      const HERO_MIN = 40;  // comfortable floor before we allow a balanced wrap
      const HERO_MAX = 208; // 13rem — keep desktop grand
      const fitHeroTitle = () => {
        const parent = heroTitle?.parentElement;
        if (!heroTitle || !parent) return;
        // Measure the name's natural single-line width at a reference size.
        heroTitle.style.whiteSpace = "nowrap";
        heroTitle.style.setProperty("text-wrap", "");
        heroTitle.style.fontSize = "100px";
        const natural = heroTitle.scrollWidth; // text width at 100px
        const cs = getComputedStyle(parent);
        const avail =
          parent.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
        if (!natural || avail <= 0) return;
        const ideal = (avail / natural) * 100 * 0.93; // 0.93 leaves serif breathing room
        if (ideal >= HERO_MIN) {
          // Fits comfortably on one line — scale to fill, capped for desktop.
          heroTitle.style.whiteSpace = "nowrap";
          heroTitle.style.fontSize = `${Math.min(HERO_MAX, ideal)}px`;
        } else {
          // Too long for one comfortable line (e.g. a long "A & B" on a tiny
          // phone) — allow a BALANCED wrap at the floor instead of an awkward
          // half-name dropping below.
          heroTitle.style.whiteSpace = "normal";
          heroTitle.style.setProperty("text-wrap", "balance");
          heroTitle.style.fontSize = `${HERO_MIN}px`;
        }
      };
      fitHeroTitle();

      // Hero başlık yeniden-ölçümü + lookbook yeniden-kurulumu tek bir
      // genişlik-korumalı dinleyiciye taşındı (aşağıda) — ikisi de yalnız
      // GENİŞLİĞE bağlı, klavye/adres-çubuğu yükseklik değişimlerinde çalışmamalı.
      let heroResizeTimer: ReturnType<typeof setTimeout> | undefined;

      // ── HERO 4-stage reveal ─────────────────────────────────────────
      // Plays on mount. PremiumLayoutHost only mounts this layout once media is
      // ready, so the intro is seen (never spent behind the loader).
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".onx-meta-top", { opacity: 0, y: -20, duration: 0.8 })
        .from(".onx-meta-bot", { opacity: 0, y: 20, duration: 0.8 }, "<")
        .from(".onx-marquee-top", { x: "100%", duration: 1.4 }, 0.2)
        .from(".onx-marquee-bot", { x: "-100%", duration: 1.4 }, 0.2);

      const titleSplit = new SplitText(".onx-hero-title", { type: "chars,lines" });
      // Re-fit now that SplitText wraps the chars — the split markup measures a
      // touch wider than the raw text, so this corrects any sub-pixel overhang.
      fitHeroTitle();
      tl.from(titleSplit.chars, {
        y: "100%",
        rotateZ: 12,
        opacity: 0,
        stagger: 0.05,
        duration: 1.2,
      }, 0.6);

      tl.from(".onx-crimson-dot", { scale: 0, duration: 0.6, ease: "back.out(2)" }, 1.4);

      // Kaydırma ipucu hero'nun EN SONUNDA belirir (isim yerleşmeden davet
      // etmek erken), kullanıcı kaydırmaya başlayınca söner. `scrub` ile
      // kaydırma konumuna bağlı: hızlı kaydırmada atlanmaz, geri gelince
      // yeniden görünür.
      tl.from(".onx-scroll-cue", { opacity: 0, y: -12, duration: 0.9 }, 2.0);
      gsap.to(".onx-scroll-cue", {
        opacity: 0,
        duration: 1,
        ease: "none",
        scrollTrigger: { trigger: ".onx-hero-title", start: "top top", end: "+=180", scrub: true },
      });

      // ── SIGNATURE MOMENT: title splits, image reveals ──────────────
      const splitTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".onx-split-section",
          start: "top top",
          end: "+=200%",
          pin: true,
          scrub: 1,
        },
      });

      splitTl
        .to(".onx-split-top", { y: "-90%", duration: 1 }, 0)
        .to(".onx-split-bot", { y: "90%", duration: 1 }, 0)
        .to(".onx-split-img-zoom", { scale: 1.0, duration: 1.4 }, 0)
        .to(".onx-split-caption", { opacity: 1, y: 0, duration: 0.5 }, 0.5)
        .to(".onx-split-top", { y: "-65%", duration: 0.4 }, 1.2)
        .to(".onx-split-bot", { y: "65%", duration: 0.4 }, 1.2);

      // ── LOOK BOOK: horizontal pinned with per-look animations ──────
      // Every look is sized in `vw`, so its rendered width depends on the
      // viewport. We rebuild the whole pin/tween/per-look-trigger set from
      // scratch on resize instead of relying on GSAP's refresh-time
      // recalculation: that only re-evaluates function-based `start`/`end`,
      // not the tween's own `x` target, which left the horizontal scroll
      // pinned to a stale (pre-resize) distance — cutting off the last
      // look(s) after any window resize or orientation change.
      const lookbook = document.querySelector<HTMLElement>(".onx-lookbook");
      const lookbookInner = document.querySelector<HTMLElement>(".onx-lookbook-inner");
      let resizeTimer: ReturnType<typeof setTimeout> | undefined;
      let rebuildLookbook: (() => void) | undefined;

      if (lookbook && lookbookInner) {
        let horizTween: gsap.core.Tween | null = null;

        const buildLookbookScroll = () => {
          horizTween?.scrollTrigger?.kill();
          horizTween?.kill();

          const distance = lookbookInner.scrollWidth - window.innerWidth;
          horizTween = gsap.to(lookbookInner, {
            x: -distance,
            ease: "none",
            scrollTrigger: {
              trigger: lookbook,
              start: "top top",
              end: `+=${distance}`,
              pin: true,
              scrub: 1,
            },
          });

          document.querySelectorAll(".onx-look").forEach((look) => {
            gsap.from(look.querySelector(".onx-look-img"), {
              scale: 1.15,
              ease: "none",
              scrollTrigger: {
                trigger: look,
                containerAnimation: horizTween!,
                start: "left right",
                end: "right left",
                scrub: true,
              },
            });
            // Caption text (num + details) is ALWAYS visible — never animated.
            // A `containerAnimation` scrub `from({opacity:0})` reveal proved
            // fragile on some phones (e.g. Galaxy S24 Ultra): the scrub never
            // advanced, leaving the text stuck at opacity 0 while the static
            // crimson rule (un-animated) still showed. Reliability > a fade.
          });
        };

        buildLookbookScroll();
        rebuildLookbook = buildLookbookScroll;
      }

      // ── Tek, genişlik-korumalı resize dinleyici ────────────────────
      // Hem hero başlık ölçümü hem lookbook mesafesi yalnız GENİŞLİĞE bağlı.
      // Yükseklik-only değişimleri (mobil klavye / adres çubuğu) yok sayılır —
      // aksi halde her klavye açılışı pin'leri yeniden kurup sayfayı sıçratıyordu.
      let lastWidth = window.innerWidth;
      onResize = () => {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        clearTimeout(heroResizeTimer);
        heroResizeTimer = setTimeout(fitHeroTitle, 150);
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          rebuildLookbook?.();
          ScrollTrigger.refresh();
        }, 200);
      };
      window.addEventListener("resize", onResize);

      // ── DATE STATEMENT ─────────────────────────────────────────────
      // Ambient Ken Burns on the faint backdrop image — scrubbed across the
      // whole section so the background slowly "breathes" as you scroll.
      if (document.querySelector(".onx-date-bg")) {
        gsap.fromTo(".onx-date-bg", { scale: 1.18, yPercent: -4 }, {
          scale: 1, yPercent: 4, ease: "none",
          scrollTrigger: { trigger: ".onx-date-section", start: "top bottom", end: "bottom top", scrub: 1 },
        });
      }
      const dateSplit = new SplitText(".onx-date-big", { type: "chars" });
      reveal(dateSplit.chars, { y: -120, opacity: 0, stagger: { each: 0.1, from: "start" } }, {
        trigger: ".onx-date-section",
        start: "top 80%",
        end: "center 65%",
      });
      reveal(".onx-date-meta", { y: 30, opacity: 0, stagger: 0.12 }, {
        trigger: ".onx-date-section",
        start: "top 75%",
        end: "center 60%",
      });
      if (document.querySelector(".onx-countdown")) {
        reveal(".onx-cd-unit", { y: 40, opacity: 0, stagger: 0.1 }, {
          trigger: ".onx-countdown",
          start: "top 90%",
          end: "top 55%",
        });
      }

      // ── STORY ──────────────────────────────────────────────────────
      reveal(".onx-story-img", { clipPath: "inset(0 100% 0 0)" }, {
        trigger: ".onx-story-section",
        start: "top 80%",
        end: "top 40%",
      });
      reveal(".onx-story-line", { y: 40, opacity: 0, stagger: 0.08 }, {
        trigger: ".onx-story-section",
        start: "top 80%",
        end: "center 70%",
      });

      // ── VENUE ──────────────────────────────────────────────────────
      // Ambient Ken Burns (scale + slow pan) on the media layer — works for the
      // <img> fallback AND the optional <video>, both carry `.onx-venue-img`.
      if (document.querySelector(".onx-venue-img")) {
        gsap.fromTo(".onx-venue-img", { scale: 1.18, xPercent: -3 }, {
          scale: 1, xPercent: 3, ease: "none",
          scrollTrigger: { trigger: ".onx-venue-section", start: "top bottom", end: "bottom top", scrub: 1 },
        });
      }
      reveal(".onx-venue-label", { y: 40, opacity: 0, stagger: 0.15 }, {
        trigger: ".onx-venue-section",
        start: "top 75%",
        end: "top 40%",
      });

      ScrollTrigger.create({
        trigger: "main",
        start: "top top",
        end: "bottom bottom",
        onUpdate(self) {
          if (progressDotRef.current) {
            progressDotRef.current.style.top = `${10 + self.progress * 80}%`;
          }
        },
      });

      reveal(".onx-form-wrapper", { opacity: 0, y: 40 }, {
        trigger: ".onx-form-wrapper",
        start: "top 85%",
        end: "top 55%",
      });
    });

    // Media is already preloaded by the host and fonts may have just settled;
    // refresh once on the next frame so pinned/scrub measurements are accurate.
    requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      if (onResize) window.removeEventListener("resize", onResize);
      detachKeyboardFix();
      ScrollTrigger.config({ autoRefreshEvents: DEFAULT_AUTO_REFRESH });
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      gctx.revert();
    };
  }, [cfg]);

  const d = dateTime ? new Date(dateTime) : null;
  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  // İki etkinlikli düğünde biri geçmişse hiyerarşi devreye girer: geçmiş
  // etkinlik soluklaşır (arka plan), asıl gelecek etkinlik öne çıkar. İkisi de
  // gelecekteyse ayrım yapılmaz (ikisi de eşit ağırlıkta). Boyut değişmez —
  // yalnız opaklık/renk, grid hizası korunur.
  const anyEventPast = program.some((e) => e.daysUntil < 0);

  // Venue: first comma-part is the venue name, the rest is the address.
  const venueParts = (location ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const venueName = venueParts[0] || (location ?? "—");
  const venueAddress = venueParts.slice(1).join(", ");

  return (
    <main
      className="relative overflow-x-hidden text-white"
      style={{ background: bg, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`
        @keyframes onxScrollX { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .onx-accent { color: ${accent}; }
        .onx-accent-bg { background: ${accent}; }
        /* Kaydırmayla scale ANIMASYONLU + grayscale FILTRELI gorseller: kendi
           GPU katmanlarina terfi ettir. Aksi halde tarayici, her scale karesinde
           filtreyi yeniden rasterize ediyor -> lookbook'ta yatay kaydirirken
           takilma. will-change:transform ile filtre bir kez rasterize edilir,
           scale ucuz bir GPU donusumu olur. */
        .onx-look-img, .onx-split-img-zoom { will-change: transform; backface-visibility: hidden; }
        /* Hero name: initial size + nowrap so first paint never wraps; the
           effect then fits it precisely to the viewport (and owns the inline
           font-size thereafter, surviving React re-renders). */
        .onx-hero-title { font-size: clamp(2.5rem, 12vw, 13rem); white-space: nowrap; }
        /* Lookbook caption: bottom overlay on phones (gradient handles contrast),
           solid side panel from sm+ (matches the desktop 2/3 image + 1/3 panel). */
        @media (min-width: 640px) { .onx-look-caption { background: ${bg}; } }

        /* Kaydırma ipucu: temanın kendi motifi — kırmızı bir ışık huzmesi ince
           hat boyunca aşağı akıp söner. Jenerik zıplayan ok yerine hero'daki ve
           scroll göstergesindeki kırmızı vurgunun devamı.
           6px'lik düz nokta koyu zeminde fark edilmiyordu; kuyruklu huzme
           hareketi okunur kılıyor (asıl amaç kaydırmaya teşvik). */
        @keyframes onxScrollCue {
          0%   { transform: translate(-50%, -100%); opacity: 0; }
          20%  { opacity: 1; }
          75%  { opacity: 1; }
          100% { transform: translate(-50%, 340%); opacity: 0; }
        }
        .onx-scroll-beam { animation: onxScrollCue 2.4s cubic-bezier(0.45, 0, 0.2, 1) infinite; }
        /* Hareket hassasiyeti: animasyon yerine sabit, sönük bir iz kalır. */
        @media (prefers-reduced-motion: reduce) {
          .onx-scroll-beam { animation: none; transform: translate(-50%, 90%); opacity: 0.7; }
        }
      `}</style>

      {/* Crimson scroll marker */}
      <div
        ref={progressDotRef}
        className="fixed right-6 z-50 w-2 h-2 rounded-full pointer-events-none transition-[top] duration-200"
        style={{ top: "10%", background: accent, boxShadow: `0 0 16px ${accent}` }}
      />

      {/* ═══ HERO ═══════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee */}
        <div className="onx-marquee-top relative h-12 border-b border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.6em] uppercase" style={{ animation: "onxScrollX 30s linear infinite" }}>
            {Array(8).fill(null).map((_, i) => (
              <span key={i} className="mx-8">
                {t(cfg.marquee.top).split("●").map((part, idx, arr) => (
                  <span key={idx}>
                    {part.trim()}
                    {idx < arr.length - 1 && <span className="mx-2" style={{ color: accent }}>●</span>}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* Merkez içerik + kaydırma ipucu. İpucu, dikey ortalanan içeriğin
            ALTINDA kendi ayrılmış satırında (shrink-0) durur; böylece iki
            etkinlikli tarih bloğu masaüstünde onunla ASLA çakışmaz (eski
            `absolute` konumda kısa masaüstlerinde tarihlerin üstüne biniyordu). */}
        <div className="flex flex-1 flex-col min-h-0">
        <div className="relative flex flex-1 flex-col items-center justify-center px-6 min-h-0">
          <div className="onx-meta-top text-center mb-12">
            <p className="text-[10px] uppercase tracking-[0.5em] text-white/40 mb-3">{t(cfg.hero.collectionLabel)}</p>
            <p className="text-[10px] uppercase tracking-[0.5em] text-white/40">{t(cfg.hero.issueLabel)}</p>
          </div>

          <h1
            className="onx-hero-title font-light text-center leading-[0.85] tracking-[-0.04em] overflow-hidden max-w-full"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {title}
          </h1>

          <div className="onx-crimson-dot mt-12 w-3 h-3 rounded-full" style={{ background: accent, boxShadow: `0 0 20px ${accent}66` }} />

          {/*
            İKİ ETKİNLİK: hero de tarih bölümüyle aynı dili konuşur — iki grup,
            aralarında kısa ve iki ucu solan bir ayraç. Salon adı yerine ŞEHİR
            gösterilir: hero dar bir alan ve asıl ayırt edici bilgi şehir
            (davetiye iki şehre birden dağıtılıyor).
            TEK ETKİNLİK: bugünkü tek satırlık düzen aynen korunur.
          */}
          {program.length > 1 ? (
            /*
              HİYERARŞİ: her etkinlik TEK okunur ifade.
              Önceki deneme altı parçayı (ad/tarih/şehir × 2) neredeyse aynı
              boyut, aynı harf aralığı ve aynı soluklukta yan yana koyuyordu →
              göz nereye bakacağını bilemiyor, blok hem karışık hem yorucu
              görünüyordu. Şimdi: ad minik+soluk (etiket), tarih+şehir Playfair
              ile belirgin (asıl bilgi) ve TEK satırda. Yıl yok — hemen yukarıda
              "№ {year}" olarak zaten duruyor.
              Masaüstü ve mobil AYNI düzen: responsive ayrışma yok.
            */
            <div
              className="onx-meta-bot mx-auto mt-12 grid w-fit grid-cols-[auto_auto_auto] items-baseline gap-x-3"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {program.map((e, i) => {
                const isPast = e.daysUntil < 0;
                // Geçmiş bir kardeş varsa öne çıkan etkinlik budur.
                const isActive = !isPast && anyEventPast;
                return (
                <Fragment key={e.key}>
                  {/* Etiket üç sütunu birden kaplar → eksende ortalı kalır.
                      Aktif etkinlikte accent renge döner (öne çıkar), geçmişte
                      daha da soluklaşır (arka plana geçer). */}
                  <p
                    className={`col-span-3 text-center font-sans text-[9px] tracking-[0.5em] uppercase ${
                      isPast ? "text-white/20" : "text-white/30"
                    } ${i > 0 ? "mt-7" : ""}`}
                    style={isActive ? { color: accent } : undefined}
                  >
                    {e.name}
                  </p>

                  {/*
                    ⚠️ HİZALAMA + ORTALAMA, ikisi birden.
                    Sadece `text-center`: satır genişlikleri farklı olduğu için
                    tarihler ve "·"ler hizalanmıyor, blok tesadüfi görünüyordu.
                    `1fr auto 1fr`: eksen sayfanın tam ortasına oturuyor ama
                    solda uzun (tarih) sağda kısa (şehir) metin olduğu için
                    GÖRÜNEN blok sola kaymış duruyordu.
                    Çözüm: `w-fit` + `mx-auto` — sütunlar içeriğe göre daralıyor,
                    satırlar ortak eksende kalıyor VE blok gerçekten ortalanıyor.
                  */}
                  {/* whitespace-nowrap: "23 Ağustos" tek satırda kalsın. Grid
                      `auto` sütunu gerçek font metrikleriyle (iOS Playfair)
                      tarihten dar hesaplanınca "Ağustos" alta düşüyordu; blok
                      zaten dar + ortalı, yanında bolca boşluk var → tek satır
                      güvenle sığıyor. */}
                  <p
                    className={`mt-2.5 whitespace-nowrap text-right font-light ${
                      isPast ? "text-white/40" : isActive ? "text-white/95" : "text-white/85"
                    }`}
                    style={{ fontSize: "clamp(1.15rem, 2.6vw, 1.6rem)" }}
                  >
                    {e.dayMonthLabel}
                  </p>
                  <span
                    aria-hidden="true"
                    className={`mt-2.5 font-light ${isPast ? "text-white/10" : "text-white/25"}`}
                    style={{ fontSize: "clamp(1.15rem, 2.6vw, 1.6rem)" }}
                  >
                    {e.cityLabel ? "·" : ""}
                  </span>
                  <p
                    className={`mt-2.5 whitespace-nowrap text-left font-light ${
                      isPast ? "text-white/20" : "text-white/45"
                    }`}
                    style={{ fontSize: "clamp(1.15rem, 2.6vw, 1.6rem)" }}
                  >
                    {e.cityLabel}
                  </p>

                  {/* Tamamlanan etkinlik: tüm satırın (tarih·şehir) altında,
                      üç sütunu birden kaplayan minik accent "GERÇEKLEŞTİ"
                      etiketi. Çizgi/iptal çağrışımı yok — açık, olumlu bir mühür. */}
                  {isPast && (
                    <p
                      className="col-span-3 mt-2 text-center font-sans text-[8px] tracking-[0.45em] uppercase"
                      style={{ color: accent, opacity: 0.85 }}
                    >
                      {tr("wedding.program.past")}
                    </p>
                  )}
                </Fragment>
                );
              })}
            </div>
          ) : (
            <div className="onx-meta-bot text-center mt-12">
              <p className="text-sm tracking-[0.5em] uppercase text-white/70">{ctx.dayMonth}.{ctx.year}</p>
              <p className="text-[10px] tracking-[0.4em] uppercase text-white/40 mt-3">{ctx.locationCity}</p>
            </div>
          )}
        </div>

          {/*
            KAYDIRMA İPUCU — temanın kendi motifi.
            Jenerik zıplayan chevron yerine kırmızı nokta ince bir hat boyunca
            aşağı akıp sönüyor: aynı nokta hero'nun ortasında ve sağdaki scroll
            göstergesinde de var, yani yeni bir görsel öge icat edilmiyor.
            Kullanıcı kaydırmaya başlayınca sönüyor (aşağıdaki ScrollTrigger) —
            görevini yapmış bir ipucunun ekranda kalması gürültüdür.
          */}
          <div className="onx-scroll-cue flex shrink-0 flex-col items-center gap-3 pb-6 sm:pb-10">
            {cfg.hero.scrollCue && (
              <span className="text-[9px] tracking-[0.5em] uppercase text-white/25">
                {t(cfg.hero.scrollCue)}
              </span>
            )}
            <span
              aria-hidden="true"
              className="relative block h-12 w-px overflow-hidden sm:h-14"
              style={{ background: "linear-gradient(to bottom, rgba(255,255,255,0.16), rgba(255,255,255,0.04))" }}
            >
              <span
                className="onx-scroll-beam absolute left-1/2 top-0 block h-4 w-px"
                style={{
                  background: `linear-gradient(to bottom, transparent, ${accent})`,
                  boxShadow: `0 0 8px ${accent}66`,
                }}
              />
            </span>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="onx-marquee-bot relative h-12 border-t border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.6em] uppercase" style={{ animation: "onxScrollX 40s linear infinite reverse" }}>
            {Array(8).fill(null).map((_, i) => (
              <span key={i} className="mx-8 text-white/50">
                {t(cfg.marquee.bottom).split("●").map((part, idx, arr) => (
                  <span key={idx}>
                    {part.trim()}
                    {idx < arr.length - 1 && <span className="mx-2" style={{ color: accent }}>●</span>}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SIGNATURE MOMENT ═══════════════════════════════════════════ */}
      <section className="onx-split-section relative h-screen overflow-hidden" style={{ background: bg }}>
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          {/* Visible from the start; the opaque z-20 panels mask it, so it's
              revealed exactly as the names slide apart (no separate fade lag). */}
          <div className="onx-split-img w-full h-full">
            <img
              src={sizedUrl(pickImage(cfg.splitMoment.imageIndex), ONYX_IMG_W.split)}
              alt=""
              className="onx-split-img-zoom w-full h-full object-cover"
              style={{ filter: "grayscale(1) contrast(1.1)", transform: "scale(1.3)" }}
            />
          </div>
        </div>

        <div className="onx-split-caption absolute inset-0 z-10 flex items-center justify-center pointer-events-none opacity-0" style={{ transform: "translateY(20px)" }}>
          <div className="text-center px-6">
            <p className="text-[10px] uppercase tracking-[0.6em] text-white/50 mb-4">{t(cfg.splitMoment.label)}</p>
            <p className="font-light italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
              "{t(cfg.splitMoment.quote)}"
            </p>
          </div>
        </div>

        <div className="onx-split-top absolute top-0 left-0 right-0 z-20 flex items-end justify-center pb-12" style={{ height: "50%", background: bg }}>
          <h2 className="font-light leading-none tracking-[-0.04em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 13rem)", transform: "translateY(50%)" }}>
            {ctx.titleFirst || title}
          </h2>
        </div>

        <div className="onx-split-bot absolute bottom-0 left-0 right-0 z-20 flex items-start justify-center pt-12" style={{ height: "50%", background: bg }}>
          <h2 className="font-light leading-none tracking-[-0.04em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 13rem)", transform: "translateY(-50%)" }}>
            {ctx.titleSecond}
          </h2>
        </div>
      </section>

      {/* ═══ LOOKBOOK ═══════════════════════════════════════════════════ */}
      {/* One horizontal pinned-scrub mechanism for every viewport. Card
          internals are responsive via CSS only: phones get a full-bleed image
          with a bottom gradient + full-width caption overlay (text fits — no
          cramped 1/3 column); sm+ keeps the 2/3 image + 1/3 side panel. */}
      <section className="onx-lookbook relative h-screen overflow-hidden">
        <div className="absolute top-6 left-6 right-6 z-30 flex justify-between items-baseline pointer-events-none">
          <p className="text-[10px] uppercase tracking-[0.5em] text-white/50">{t(cfg.lookbook.headerLabel)}</p>
          <p className="text-[10px] uppercase tracking-[0.5em] text-white/30">{t(cfg.lookbook.headerSeason)}</p>
        </div>

        <div className="onx-lookbook-inner absolute top-0 left-0 h-full flex" style={{ width: "max-content" }}>
          {/* Intro panel */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-8 sm:px-12">
            <div className="max-w-md">
              <p className="text-[10px] uppercase tracking-[0.5em] text-white/40 mb-6">{t(cfg.lookbook.forewordLabel)}</p>
              <p
                className="font-light italic leading-tight text-[length:clamp(1.5rem,6vw,2.5rem)] sm:text-[length:clamp(1.5rem,3vw,2.5rem)]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t(cfg.lookbook.forewordText)}
              </p>
            </div>
          </div>

          {looks.map((look, i) => (
            <div
              key={i}
              className="onx-look flex-shrink-0 h-full relative flex items-end sm:items-stretch w-screen sm:w-[90vw]"
            >
              {/* image: full-bleed on phones, left 2/3 column from sm+ */}
              <div className="absolute inset-0 sm:static sm:w-2/3 sm:h-full overflow-hidden">
                <img src={sizedUrl(look.img, ONYX_IMG_W.look)} alt="" decoding="async" className="onx-look-img w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
              </div>
              {/* phone-only scrim for caption legibility over the image */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent sm:hidden" />
              {/* caption: bottom overlay on phones, right 1/3 panel from sm+ */}
              <div className="onx-look-caption relative z-10 w-full px-6 pt-6 pb-[calc(3rem+env(safe-area-inset-bottom))] sm:w-1/3 sm:h-full sm:flex sm:flex-col sm:justify-center sm:px-12 sm:py-0">
                <p className="onx-look-num text-[10px] uppercase tracking-[0.5em] text-white/60 sm:text-white/40 mb-4">{look.num}</p>
                <div className="w-12 h-px mb-4 sm:mb-8" style={{ background: accent }} />
                <p
                  className="onx-look-detail font-light leading-tight mb-2 sm:mb-3 text-[length:clamp(1.5rem,7vw,2.5rem)] sm:text-[length:clamp(1.5rem,2.5vw,2.5rem)]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {look.detail1}
                </p>
                <p className="onx-look-detail text-sm text-white/70 sm:text-white/60 mb-1 tracking-wide">{look.detail2}</p>
                <p className="onx-look-detail text-xs text-white/50 sm:text-white/40 tracking-[0.3em] uppercase mt-2 sm:mt-4">{look.detail3}</p>
              </div>
            </div>
          ))}

          {/* Outro */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-8 sm:px-12 relative" style={{ background: bg }}>
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px" style={{ background: `linear-gradient(180deg, transparent, ${accent}55, transparent)` }} />

            <div className="text-center max-w-xl">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: accent }}>{t(cfg.lookbook.outroLabel)}</p>
              <h2 className="font-light leading-none tracking-[-0.04em] mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(3rem, 8vw, 6rem)" }}>
                {t(cfg.lookbook.outroTitle)}
              </h2>
              <div className="mx-auto w-20 h-px mb-8" style={{ background: accent }} />
              <p className="italic font-light leading-snug mb-6 whitespace-pre-line" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}>
                {t(cfg.lookbook.outroQuote)}
              </p>
              <p className="text-sm text-white/60 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t(cfg.lookbook.outroSmall)}
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] uppercase tracking-[0.4em] text-white/40">
                <span>{t(cfg.lookbook.outroNavLeft)}</span>
                <span style={{ color: accent }}>◆</span>
                <span>{t(cfg.lookbook.outroNavMiddle)}</span>
                <span style={{ color: accent }}>◆</span>
                <span>{t(cfg.lookbook.outroNavRight)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DATE STATEMENT ════════════════════════════════════════════ */}
      <section className="onx-date-section relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 sm:px-16 py-28">
        {/* Ambient grayscale backdrop with scrubbed Ken Burns (asset-resilient) */}
        {dateBg && (
          <img
            src={sizedUrl(dateBg, ONYX_IMG_W.bg)}
            alt=""
            aria-hidden="true"
            className="onx-date-bg absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ filter: "grayscale(1) contrast(1.05) brightness(0.34)", transform: "scale(1.18)" }}
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(125% 85% at 50% 42%, transparent 0%, ${bg}cc 68%, ${bg} 100%)` }}
        />

        {/* Editorial header rule */}
        <div className="absolute top-8 left-6 right-6 sm:left-16 sm:right-16 flex justify-between items-baseline pointer-events-none">
          <span className="text-[10px] uppercase tracking-[0.5em] text-white/30">{t(cfg.dateSection.label)}</span>
          <span className="text-[10px] uppercase tracking-[0.5em] text-white/20">№ {ctx.year}</span>
        </div>

        {/*
          İKİ ETKİNLİK: tarih bölümü Onyx'in KENDİ tipografisiyle iki sütuna
          ayrılır — ayrı bir kutu iliştirilmez. "Asıl etkinlik" vurgusu YOK ve
          her etkinlik kendi gün sayacını taşır: davetiye QR'ı iki şehre birden
          dağıtılıyor, yalnız birine davetli misafir yanlış sayaca bakmamalı.
          TEK ETKİNLİK: bugünkü dev tarih + canlı geri sayım aynen korunur.
        */}
        {program.length > 1 ? (
          <div className="relative z-10 w-full max-w-6xl">
            {/*
              Ayraç sütun yüksekliğinin TAMAMINI kaplamıyor: Onyx'in dili kısa,
              ortalanmış çizgiler üzerine kurulu — kenardan kenara bir kural
              iki etkinliği "ayrı sayfalar" gibi gösteriyordu.
            */}
            <div
              className="onx-date-meta pointer-events-none absolute left-1/2 top-1/2 hidden h-[55%] w-px -translate-x-1/2 -translate-y-1/2 sm:block"
              style={{ background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.14), transparent)" }}
            />
            <div className="grid gap-16 sm:grid-cols-2 sm:gap-20">
              {program.map((e) => (
                <div
                  key={e.key}
                  className="flex flex-col items-center text-center transition-opacity"
                  // Geçmiş etkinlik bir bütün olarak arka plana çekilir (hero
                  // ile tutarlı); gelecek etkinlik tam ağırlıkta kalır. "geçti"
                  // etiketi içeride zaten duruyor.
                  style={{ opacity: e.daysUntil < 0 ? 0.4 : 1 }}
                >
                  <span className="onx-date-meta text-[11px] uppercase tracking-[0.45em] text-white/55">
                    {e.name}
                  </span>

                  {/*
                    Ölçek bilinçli olarak tek-etkinlik sürümünden küçük: iki
                    büyük tarih yan yana geldiğinde 13rem'lik ölçek sütunları
                    ayraca dayıyor ve "Kasım/Aralık" gibi uzun ay adları
                    taşıyordu. 5.5rem tavan iki sütunda da nefes bırakıyor.
                  */}
                  <div
                    className="onx-date-big mt-5 font-light leading-none tabular-nums tracking-[-0.04em]"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.75rem, 7.5vw, 5.5rem)" }}
                  >
                    {e.dateLabel.replace(/\s\d{4}$/, "")}
                  </div>
                  <div
                    className="font-light tabular-nums tracking-[0.3em]"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(0.95rem, 2.2vw, 1.5rem)", color: "rgba(255,255,255,0.38)" }}
                  >
                    {new Date(e.dateTime).getFullYear()}
                  </div>

                  <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[10px] uppercase tracking-[0.3em] text-white/65">
                    <span className="onx-date-meta">{e.weekdayLabel}</span>
                    <span className="onx-date-meta" style={{ color: accent }}>◆</span>
                    <span className="onx-date-meta">{e.timeLabel}</span>
                  </div>

                  {e.location && (
                    <p className="onx-date-meta mt-3 max-w-[15rem] text-[10px] uppercase tracking-[0.18em] leading-[1.9] text-white/45">
                      {e.location}
                    </p>
                  )}

                  <div className="onx-date-meta mt-7 mb-5 w-10 h-px" style={{ background: accent }} />

                  <span className="onx-date-meta text-[10px] uppercase tracking-[0.4em] text-white/55">
                    {e.daysUntil < 0
                      ? tr("wedding.program.past")
                      : e.daysUntil === 0
                        ? tr("wedding.program.today")
                        : e.daysUntil === 1
                          ? tr("wedding.program.tomorrow")
                          : tr("wedding.program.daysLeft", { count: e.daysUntil })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="relative z-10 w-full max-w-4xl flex flex-col items-center text-center">
            <div className="onx-date-big font-light leading-none tabular-nums tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5.5rem, 18vw, 13rem)" }}>
              {ctx.dayMonth}
            </div>
            <div className="font-light tabular-nums tracking-[0.3em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.75rem, 5vw, 3.5rem)", color: "rgba(255,255,255,0.4)" }}>
              {ctx.year}
            </div>

            {/* meta row: weekday · time · city */}
            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/65">
              <span className="onx-date-meta">{ctx.weekday}</span>
              <span className="onx-date-meta" style={{ color: accent }}>◆</span>
              <span className="onx-date-meta">{ctx.time}</span>
              <span className="onx-date-meta" style={{ color: accent }}>◆</span>
              <span className="onx-date-meta">{ctx.locationCity || (location ?? "—")}</span>
            </div>

            <div className="onx-date-meta mt-10 mb-10 w-16 h-px" style={{ background: accent }} />

            {activeEvent && (
              <>
                <p className="onx-date-meta text-[10px] uppercase tracking-[0.5em] text-white/40 mb-7">
                  {tr("home.countdown.labelFor", { event: activeEventName })}
                </p>
                <OnyxCountdown dateTime={activeEvent.dateTime} accent={accent} />
              </>
            )}
          </div>
        )}

        {/* Bottom marquee ticker */}
        <div className="absolute bottom-0 left-0 right-0 h-10 border-t border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-white/40" style={{ animation: "onxScrollX 35s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                {location ?? "—"}
                <span className="mx-2" style={{ color: accent }}>●</span>
                {ctx.weekday}
                <span className="mx-2" style={{ color: accent }}>●</span>
                {ctx.time}
                <span className="mx-2" style={{ color: accent }}>●</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ STORY ═════════════════════════════════════════════════════ */}
      <section className="onx-story-section relative px-6 sm:px-16 py-32">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-12 gap-8">
          <div className="sm:col-span-5 sm:sticky sm:top-32 self-start">
            <div className="onx-story-img relative overflow-hidden aspect-[3/4]">
              <img src={sizedUrl(pickImage(cfg.story.imageIndex), ONYX_IMG_W.story)} alt="" decoding="async" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
            </div>
          </div>

          <div className="sm:col-span-7 sm:pl-8">
            <p className="onx-story-line text-[10px] uppercase tracking-[0.5em] mb-8" style={{ color: accent }}>{t(cfg.story.label)}</p>
            <h2 className="onx-story-line font-light leading-[1.05] tracking-tight mb-12 whitespace-pre-line" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              {t(cfg.story.headline)}
            </h2>
            {storyLines.map((line, i) => (
              <p key={i} className="onx-story-line text-lg leading-relaxed text-white/75 mb-5 max-w-xl">
                {line.trim()}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VENUE ═════════════════════════════════════════════════════ */}
      <section className="onx-venue-section relative h-screen overflow-hidden">
        {/* Media layer: ambient looping video when provided (demo), else a
            grayscale image with scrubbed Ken Burns. Both carry .onx-venue-img. */}
        {ambientVideoUrl ? (
          <video
            src={ambientVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            className="onx-venue-img absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.1) brightness(0.6)" }}
          />
        ) : venueBg ? (
          <img
            src={sizedUrl(venueBg, ONYX_IMG_W.bg)}
            alt=""
            className="onx-venue-img absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.1) brightness(0.6)" }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40" />

        {/* Editorial header rule */}
        <div className="absolute top-8 left-6 right-6 sm:left-16 sm:right-16 flex justify-between items-baseline pointer-events-none">
          <span className="onx-venue-label text-[10px] uppercase tracking-[0.5em]" style={{ color: accent }}>{t(cfg.venue.label)}</span>
          <span className="onx-venue-label text-[10px] uppercase tracking-[0.5em] text-white/30">{ctx.locationCity}</span>
        </div>

        <div className="absolute inset-0 flex items-end px-6 sm:px-16 pb-[calc(4rem+env(safe-area-inset-bottom))] sm:pb-20">
          <div className="max-w-3xl">
            <h2 className="onx-venue-label font-light leading-[1.05] tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.75rem, 9vw, 6rem)" }}>
              {venueName}
            </h2>
            {venueAddress && (
              <p className="onx-venue-label mt-5 max-w-md text-sm sm:text-base leading-relaxed text-white/70">
                {venueAddress}
              </p>
            )}
            <div className="onx-venue-label mt-7 w-12 h-px" style={{ background: accent }} />
            <p className="onx-venue-label mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-sm tracking-[0.3em] uppercase text-white/60">
              <span>{d?.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span style={{ color: accent }}>●</span>
              <span>{d?.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
            </p>
          </div>
        </div>
      </section>

      {/* ═══ FORM ══════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32">
        <div className="onx-form-wrapper w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: accent }}>{t(cfg.rsvp.label)}</p>
            <h2 className="font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
              {t(cfg.rsvp.heading)}
            </h2>
          </div>
          {formSlot}
          <p className="text-center text-[10px] tracking-[0.4em] uppercase text-white/30 mt-10">
            {t(cfg.rsvp.closingPrefix)}
          </p>
        </div>
      </section>
    </main>
  );
}
